import { Not, NumberEquals, NumberGreaterThan, StringEquals, TimeAfter } from '@vendasta/automata';
import { DateInputDisplayService } from './dynamic-options/date-input/date-input-display.service';
import { DateInputComponent } from './dynamic-options/date-input/date-input.component';
import { EmptyStringComponent, EmptyStringDisplayService } from './dynamic-options/empty-string-component';
import { PositiveIntegerDisplayService } from './dynamic-options/positive-integer/positive-integer-display.service';
import { PositiveIntegerComponent } from './dynamic-options/positive-integer/positive-integer.component';
import { DataFilter } from './interface';

const PARTNER_STRIPE_SETUP = '{.partner_data.retail_payment_status.requirements.due}';
const PARTNER_CREATED_DATE = '{.partner_data.created_date.absolute_time}';
class PartnerStripeSetupDataFilter implements DataFilter {
  rules = [new StringEquals(PARTNER_STRIPE_SETUP), new Not(new StringEquals(PARTNER_STRIPE_SETUP))];
  dataEntryComponent = EmptyStringComponent;
  displayService = EmptyStringDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.STRIPE_SETUP';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.HAS_BEEN_SETUP',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.HAS_NOT_BEEN_SETUP',
  ];
}

class PartnerCreatedDateDataFilter implements DataFilter {
  rules = [new TimeAfter(PARTNER_CREATED_DATE)];
  dataEntryComponent = DateInputComponent;
  displayService = DateInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.CREATED_DATE';
  comparisonLabels = ['AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.AFTER'];
}

const PARTNER_SNAPSHOT_COUNT = '{.partner_data.partner_stats.generated_snapshots}';
class PartnerGeneratedSnapshotsDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(PARTNER_SNAPSHOT_COUNT)),
    new NumberEquals(PARTNER_SNAPSHOT_COUNT),
    new NumberGreaterThan(PARTNER_SNAPSHOT_COUNT),
  ];
  dataEntryComponentList = [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent];
  displayService = PositiveIntegerDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.SNAPSHOTS_CREATED';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const PARTNER_DAYS_ACTIVE_IN_PLATFORM = '{.partner_data.partner_stats.days_active_in_platform}';
class PartnerDaysActiveInPlatformDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(PARTNER_DAYS_ACTIVE_IN_PLATFORM)),
    new NumberEquals(PARTNER_DAYS_ACTIVE_IN_PLATFORM),
    new NumberGreaterThan(PARTNER_DAYS_ACTIVE_IN_PLATFORM),
  ];
  dataEntryComponentList = [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent];
  displayService = PositiveIntegerDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.DAYS_ACTIVE_IN_PLATFORM';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const PARTNER_ACTIVE_PAID_PRODUCTS_COUNT = '{.partner_data.partner_stats.active_paid_products}';
class PartnerActivePaidProductsDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(PARTNER_ACTIVE_PAID_PRODUCTS_COUNT)),
    new NumberEquals(PARTNER_ACTIVE_PAID_PRODUCTS_COUNT),
    new NumberGreaterThan(PARTNER_ACTIVE_PAID_PRODUCTS_COUNT),
  ];
  dataEntryComponentList = [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent];
  displayService = PositiveIntegerDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.ACTIVE_PRODUCTS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const PARTNER_ACCOUNTS_COUNT = '{.partner_data.partner_stats.accounts}';
class PartnerAccountsDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(PARTNER_ACCOUNTS_COUNT)),
    new NumberEquals(PARTNER_ACCOUNTS_COUNT),
    new NumberGreaterThan(PARTNER_ACCOUNTS_COUNT),
  ];
  dataEntryComponentList = [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent];
  displayService = PositiveIntegerDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.ACCOUNTS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const PARTNER_OPEN_OPPORTUNITIES_COUNT = '{.partner_data.partner_stats.open_opportunities}';
class PartnerOpenOpportunitiesCountAccountsDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(PARTNER_OPEN_OPPORTUNITIES_COUNT)),
    new NumberEquals(PARTNER_OPEN_OPPORTUNITIES_COUNT),
    new NumberGreaterThan(PARTNER_OPEN_OPPORTUNITIES_COUNT),
  ];
  dataEntryComponentList = [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent];
  displayService = PositiveIntegerDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.DATA_LABELS.OPEN_OPPORTUNITIES';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.GREATER_THAN',
  ];
}

export const PARTNER_DATA_COMPONENTS: Map<string, DataFilter> = new Map<string, DataFilter>([
  [PARTNER_STRIPE_SETUP, new PartnerStripeSetupDataFilter()],
  [PARTNER_CREATED_DATE, new PartnerCreatedDateDataFilter()],
  [PARTNER_SNAPSHOT_COUNT, new PartnerGeneratedSnapshotsDataFilter()],
  [PARTNER_DAYS_ACTIVE_IN_PLATFORM, new PartnerDaysActiveInPlatformDataFilter()],
  [PARTNER_ACTIVE_PAID_PRODUCTS_COUNT, new PartnerActivePaidProductsDataFilter()],
  [PARTNER_ACCOUNTS_COUNT, new PartnerAccountsDataFilter()],
  [PARTNER_OPEN_OPPORTUNITIES_COUNT, new PartnerOpenOpportunitiesCountAccountsDataFilter()],
]);
